import { Head, Link } from '@inertiajs/react'
import { ChevronRight, Plus, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import FdeShell from '@/frontend/features/Fde/FdeShell'
import { Banner, ResultChip, StatusPill, attentionText, targetLabel } from '@/frontend/features/Fde/triggers/components'
import { triggerSentence } from '@/frontend/features/Fde/triggers/builder'
import { AskAgent } from '@/frontend/features/Fde/triggers/agentic'
import type { Trigger, TriggersPageProps } from '@/frontend/features/Fde/triggers/types'
import { ago, attentionRank, needsAttention, withProject } from '@/frontend/features/Fde/triggers/types'

interface Props extends TriggersPageProps {
  triggers: Trigger[]
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'attention' }) {
  return (
    <div className="grid gap-1 rounded-xl border border-border bg-background px-4 py-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={cn('text-2xl font-semibold tabular-nums', tone === 'attention' && value > 0 && 'text-amber-700 dark:text-amber-300')}>{value}</span>
    </div>
  )
}

function Row({ trigger, href, copy }: { trigger: Trigger; href: string; copy: Record<string, string> }) {
  const attention = attentionText(trigger, copy)
  return (
    <Link href={href} className="group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 rounded-xl border border-border bg-background px-4 py-3.5 transition-colors hover:border-foreground/20 hover:bg-accent/30">
      <div className="grid min-w-0 gap-1.5">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="truncate font-medium">{trigger.name}</span>
          <StatusPill status={trigger.status} copy={copy} />
        </div>
        <p className="truncate text-sm text-muted-foreground">
          {triggerSentence(trigger.spec, copy)}
        </p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Zap className="size-3" aria-hidden />{targetLabel(trigger.spec, copy)}</span>
          <span>{copy.last_look} {ago(trigger.last_finished_at ?? trigger.last_started_at)}</span>
          <ResultChip result={trigger.last_status} copy={copy} />
          <span className="tabular-nums">{trigger.events_7d ?? 0} {copy.events_7d}</span>
          {(trigger.events_waiting ?? 0) > 0 && <span className="tabular-nums">{trigger.events_waiting} {copy.waiting}</span>}
        </div>
        {attention && (
          <p className={cn('truncate text-xs font-medium', attention.tone === 'error' ? 'text-destructive' : 'text-amber-800 dark:text-amber-200')}>{attention.text}</p>
        )}
      </div>
      <ChevronRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" aria-hidden />
    </Link>
  )
}

function TriggersIndex(props: Props) {
  const copy = props.triggers_copy
  const projectId = props.project?.id
  const sorted = [...props.triggers].sort((a, b) => attentionRank(a) - attentionRank(b) || a.name.localeCompare(b.name))
  const ask = <AskAgent agents={props.trigger_agents} copy={copy} template={copy.ask_template} title={copy.ask_title} label={copy.ask_agent} />
  const actions = props.can_manage && (
    <div className="flex items-center gap-2">
      <Button asChild size="sm" variant="ghost"><Link href={props.paths.new}><Plus className="size-4" aria-hidden />{copy.manual}</Link></Button>
      {ask}
    </div>
  )
  return (
    <>
      <Head title={copy.title} />
      <FdeShell copy={props.copy} eyebrow="" title={props.project?.name ?? props.organization.name} description="" projectId={projectId}>
        <div className="@container h-full overflow-y-auto bg-muted/10">
          <div className="mx-auto grid max-w-5xl gap-6 px-4 pb-16 pt-6 @3xl:px-6">
            <header className="flex flex-wrap items-start justify-between gap-4">
              <div className="grid gap-2">
                <h2 className="text-2xl font-semibold tracking-tight">{copy.title}</h2>
                <p className="max-w-3xl text-sm text-muted-foreground">{copy.lead}</p>
              </div>
              {actions}
            </header>
            {props.error && <Banner tone="error">{props.error}</Banner>}
            {sorted.length > 0 && (
              <div className="grid grid-cols-2 gap-3 @2xl:grid-cols-4">
                <Stat label={copy.summary_active} value={sorted.filter((t) => t.status === 'active').length} />
                <Stat label={copy.summary_attention} value={sorted.filter(needsAttention).length} tone="attention" />
                <Stat label={copy.summary_events} value={sorted.reduce((sum, t) => sum + (t.events_7d ?? 0), 0)} />
                <Stat label={copy.summary_dead} value={sorted.reduce((sum, t) => sum + (t.events_dead ?? 0), 0)} tone="attention" />
              </div>
            )}
            {sorted.length === 0 && !props.error ? (
              <div className="grid justify-items-center gap-3 rounded-2xl border border-dashed border-border bg-background px-6 py-14 text-center">
                <span className="grid size-10 place-items-center rounded-full bg-primary/10 text-primary"><Zap className="size-5" aria-hidden /></span>
                <h3 className="text-base font-semibold">{copy.empty_title}</h3>
                <p className="max-w-md text-sm text-muted-foreground">{copy.empty_body}</p>
              </div>
            ) : (
              <div className="grid gap-2">
                {sorted.map((trigger) => (
                  <Row key={trigger.id} trigger={trigger} copy={copy} href={withProject(`${props.paths.base}/${trigger.id}`, projectId)} />
                ))}
              </div>
            )}
          </div>
        </div>
      </FdeShell>
    </>
  )
}

TriggersIndex.layout = (page: React.ReactElement) => <DashboardLayout variant="fullscreen">{page}</DashboardLayout>
export default TriggersIndex
